import type { ChartElementValue, ChartItem } from '@vedalib/editor/lib/elements'
import { generateId } from '@vedalib/editor/lib/utils/id'
import * as R from 'ramda'
import { useCallback, useMemo } from 'react'
import React from 'react'

import { genElementValueField } from 'components/Editor/Toolbar/NodeForm/genElementValueField'
import Padding from 'components/Editor/Toolbar/Padding'
import type { IFieldConfig } from 'components/formKit'
import { chainName, FieldColorPicker, FieldInput, FieldNumber, FormField } from 'components/formKit'
import { useFormData } from 'components/formKit/hooks'
import type { IRenderItemProps } from 'components/uiKit/ArrayControl'
import { IconButton } from 'components/uiKit/Button'
import { Tooltip } from 'components/uiKit/Dropdown'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { useBrandColorOptions } from 'services/Branding/fieldConfigs/useFieldConfigs'
import type { Block } from 'services/Store/Project/types'
import { t } from 'services/Translation'

import * as s from './ChartFormValueItem.module.scss'

const useShowNotLine = ({ name }: IFieldConfig<Block>) => {
  const values = useFormData<ChartElementValue>(name, 3)
  const hidden = values?.type === 'line'
  return useMemo(() => ({ hidden }), [hidden])
}

const LABEL = genElementValueField({
  as: FieldInput,
  name: 'label',
  rules: [{ max: 30 }],
})

const VALUE = genElementValueField({
  as: FieldNumber,
  name: 'val',
  label: t('input.label.value'),
  defaultValue: 0,
  props: { width: 64 },
})

const COLOR = genElementValueField({
  as: FieldColorPicker<string | null>,
  name: 'color',
  label: t('elements.chart.form.lineColor'),
  props: { labeled: true },
  useConfigs: [useBrandColorOptions, useShowNotLine],
})

const ChartFormValueItem: React.FC<IRenderItemProps<ChartItem>> = ({
  index,
  name,
  drag,
  onChange,
  value: array,
  min,
  max,
}) => {
  const disableClone = Boolean(max && array.length >= max)
  const disableDelete = Boolean(min && array.length <= min)

  const onClone = useCallback(
    () =>
      onChange?.([...array, { ...R.clone(array[index]), value: generateId() }], {
        command: 'clone',
        item: array[index],
      }),
    [onChange, array, index],
  )

  const onDelete = useCallback(
    () =>
      onChange?.(
        array.filter((_item, i) => i !== index),
        { command: 'delete', item: array[index] },
      ),
    [onChange, array, index],
  )

  return (
    <div className={s.root}>
      <div className={s.header} ref={drag}>
        <span className={s.title}>
          <Icon name='builderDrugAndDrop' size={KitSize.S} />
          {t('input.label.point')} {index + 1}
        </span>
        <span className={s.actions}>
          <Tooltip overlay={t('uiKit.tooltip.duplicate')}>
            <IconButton
              disabled={disableClone}
              icon='otherCopy'
              name='clone'
              onClick={onClone}
              size={KitSize.XS}
              styleType='ghost'
            />
          </Tooltip>
          <Tooltip overlay={t('uiKit.tooltip.delete')}>
            <IconButton
              disabled={disableDelete}
              icon='otherTrash'
              name='delete'
              onClick={onDelete}
              size={KitSize.XS}
              styleType='ghost'
            />
          </Tooltip>
        </span>
      </div>
      <Padding bottom gap sides top>
        <FormField config={chainName(name, `${index}`, LABEL)} />
        <FormField config={chainName(name, `${index}`, VALUE)} />
        <FormField config={chainName(name, `${index}`, COLOR)} />
      </Padding>
    </div>
  )
}

export default React.memo(ChartFormValueItem) as typeof ChartFormValueItem
